import { useField } from 'formik';
import { FormsGroup } from './FormsGroup';
import { useFormLabel } from './FormsI18nContext';
import React, { FC, useEffect, useMemo } from 'react';
import TextArea from 'antd/lib/input/TextArea';
import type { AutoSizeType } from 'rc-textarea';
import Dictaphone, { VoiceProps } from '../Dictaphone/Dictaphone';
import styles from './Forms.module.scss';

export type FormsTextAreaProps = VoiceProps & {
  name: string;
  label?: string | false;
  autoSize?: boolean | AutoSizeType;
  required?: boolean;
  className?: string;
  placeholder?: string;
  rows?: number;
  disabled?: boolean;
};

export const FormsTextArea: FC<FormsTextAreaProps> = ({
  name,
  label: labelOverride,
  autoSize,
  required,
  className,
  placeholder,
  rows,
  disabled,
  enableVoice,
  activeVoiceId,
  setActiveVoiceId,
}: FormsTextAreaProps) => {
  const [field, , helpers] = useField(name);
  const label = useFormLabel(name, labelOverride);

  function handleChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const value = e.target.value;

    if (value?.length === 0) {
      helpers.setValue(null);
    } else {
      field.onChange(e);
    }
  }

  const id = useMemo(() => `dictaphone-${name}`, [name]);

  function handleOnBlur(e: React.ChangeEvent<HTMLTextAreaElement>) {
    const value = e.target.value?.trim();
    helpers.setValue(value);
  }

  useEffect(() => {
    const value = field.value?.trim();
    helpers.setValue(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleTranscript(text: string) {
    if (text && field.value !== text)
      helpers.setValue(((field.value ?? '') + ' ' + text).trim());
  }

  return (
    <FormsGroup label={label} name={name} required={required}>
      <div className={enableVoice && !disabled ? styles.text : undefined}>
        <TextArea
          {...field}
          value={field.value ?? ''}
          onChange={handleChange}
          onBlur={handleOnBlur}
          autoSize={autoSize}
          className={className}
          placeholder={placeholder}
          rows={rows}
          disabled={disabled}
          bordered={!(enableVoice && !disabled)}
        />
        {enableVoice && !disabled && setActiveVoiceId && (
          <Dictaphone
            onTranscript={handleTranscript}
            setActiveVoiceId={setActiveVoiceId}
            activeVoiceId={activeVoiceId ?? null}
            id={id}
          />
        )}
      </div>
    </FormsGroup>
  );
};

FormsTextArea.defaultProps = {
  autoSize: {
    minRows: 3,
    maxRows: 10,
  },
};
